import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

const AVAILABLE_FIELDS = [
  { token: '{phone}', label: 'Phone (10 digits)', example: '1234567890' },
  { token: '{phone_formatted}', label: 'Phone (formatted)', example: '(123) 456-7890' },
  { token: '{agent}', label: 'Agent ID', example: 'jsmith' },
  { token: '{agent_email}', label: 'Agent Email', example: 'jsmith@company.com' },
  { token: '{date}', label: 'Date (YYYY-MM-DD)', example: '2026-03-27' },
  { token: '{date_short}', label: 'Date (MMDDYYYY)', example: '03272026' },
  { token: '{time_12h}', label: 'Time (12h)', example: '02_30_45 PM' },
  { token: '{time_24h}', label: 'Time (24h)', example: '14_30_45' },
  { token: '{direction}', label: 'Direction', example: 'in' },
  { token: '{system}', label: 'System', example: 'pulsedial' },
  { token: '{callid}', label: 'Call ID', example: 'CA1234abcd' },
  { token: '{duration}', label: 'Duration (sec)', example: '325' },
  { token: '{ext}', label: 'File Extension', example: 'mp3' },
];

interface ExportFormat {
  id: string;
  name: string;
  pattern: string;
}

function ExportFormats() {
  const [formats, setFormats] = useState<ExportFormat[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [pattern, setPattern] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const loadFormats = () => {
    setLoading(true);
    api.listExportFormats()
      .then((data: any) => setFormats(data.formats || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadFormats(); }, []);

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const previewPattern = (p: string): string => {
    return p
      .replace(/\{phone\}/g, '1234567890')
      .replace(/\{phone_formatted\}/g, '(123) 456-7890')
      .replace(/\{agent\}/g, 'jsmith')
      .replace(/\{agent_email\}/g, 'jsmith@company.com')
      .replace(/\{date\}/g, '2026-03-27')
      .replace(/\{date_short\}/g, '03272026')
      .replace(/\{time_12h\}/g, '02_30_45 PM')
      .replace(/\{time_24h\}/g, '14_30_45')
      .replace(/\{direction\}/g, 'in')
      .replace(/\{system\}/g, 'pulsedial')
      .replace(/\{callid\}/g, 'CA1234abcd')
      .replace(/\{duration\}/g, '325')
      .replace(/\{ext\}/g, 'mp3');
  };

  const insertField = (token: string) => {
    setPattern(prev => prev + token);
  };

  const handleSave = async () => {
    if (!name.trim() || !pattern.trim()) {
      showMessage('Name and pattern are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.saveExportFormat(name.trim(), pattern.trim(), editingId || undefined);
      showMessage(editingId ? 'Format updated' : 'Format created', 'success');
      setName('');
      setPattern('');
      setEditingId(null);
      loadFormats();
    } catch (err: any) {
      showMessage(err.message || 'Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleEdit = (fmt: ExportFormat) => {
    setName(fmt.name);
    setPattern(fmt.pattern);
    setEditingId(fmt.id);
  };

  const handleDelete = async (fmt: ExportFormat) => {
    if (!window.confirm(`Delete format "${fmt.name}"?`)) return;
    try {
      await api.deleteExportFormat(fmt.id);
      showMessage('Format deleted', 'success');
      loadFormats();
    } catch (err: any) {
      showMessage(err.message || 'Failed to delete', 'error');
    }
  };

  const handleCancel = () => {
    setName('');
    setPattern('');
    setEditingId(null);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Export Formats</h1>
        <p>Create custom naming conventions for downloading recordings</p>
      </div>

      {message && (
        <div style={{
          padding: '12px 16px', marginBottom: '16px', borderRadius: '6px',
          background: message.type === 'success' ? '#e8f5e9' : '#ffebee',
          color: message.type === 'success' ? '#2e7d32' : '#c62828',
          border: `1px solid ${message.type === 'success' ? '#a5d6a7' : '#ef9a9a'}`,
        }}>
          {message.text}
        </div>
      )}

      {/* Format builder */}
      <div style={{ background: '#fff', borderRadius: '8px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <h3 style={{ marginTop: 0, marginBottom: '12px' }}>
          {editingId ? 'Edit Format' : 'Create Format'}
        </h3>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Format name (e.g. SMS, Medicare Upline)"
            style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
          />
        </div>
        <div style={{ marginBottom: '8px' }}>
          <input
            type="text"
            value={pattern}
            onChange={e => setPattern(e.target.value)}
            placeholder="Pattern — type text and click fields below to insert"
            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', fontFamily: 'monospace', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {AVAILABLE_FIELDS.map(f => (
            <button
              key={f.token}
              type="button"
              onClick={() => insertField(f.token)}
              title={`Inserts ${f.token} — e.g. ${f.example}`}
              style={{
                padding: '4px 10px', borderRadius: '4px', border: '1px solid #80deea',
                background: '#e0f7fa', color: '#0097a7', fontSize: '12px', cursor: 'pointer',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        {pattern && (
          <div style={{ padding: '10px 14px', background: '#f8f9fa', borderRadius: '6px', marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', color: '#888' }}>Preview: </span>
            <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{previewPattern(pattern)}</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: saving ? '#999' : '#00bcd4', color: '#fff', fontSize: '14px', cursor: 'pointer' }}
          >
            {saving ? 'Saving...' : editingId ? 'Update' : 'Save Format'}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={handleCancel}
              style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', fontSize: '14px', cursor: 'pointer' }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Saved formats */}
      {loading ? (
        <div className="loading">Loading...</div>
      ) : formats.length === 0 ? (
        <div className="setup-card" style={{ textAlign: 'center', color: '#666' }}>
          <p>No export formats created yet. Build one above using the field buttons.</p>
          <p style={{ fontSize: '13px', color: '#999' }}>
            Example for Medicare upline: click <strong>Phone</strong>, type <code> by </code>, click <strong>Agent Email</strong>, type <code> @ </code>, click <strong>Time (12h)</strong>, type <code>.</code>, click <strong>File Extension</strong>
          </p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Pattern</th>
              <th>Preview</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {formats.map(fmt => (
              <tr key={fmt.id}>
                <td style={{ fontWeight: 600 }}>{fmt.name}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{fmt.pattern}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#666' }}>{previewPattern(fmt.pattern)}</td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="button" onClick={() => handleEdit(fmt)}
                      style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #80deea', background: '#e0f7fa', color: '#0097a7', fontSize: '12px', cursor: 'pointer' }}>
                      Edit
                    </button>
                    <button type="button" onClick={() => handleDelete(fmt)}
                      style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #ef9a9a', background: '#fff', color: '#c62828', fontSize: '12px', cursor: 'pointer' }}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default ExportFormats;
